/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useState } from 'react';
import { hasExtendedFilter } from '@utils/survey/getDisableUtrs';
import { ScopeView, ScopeViewDropdown } from './partials/ScopeViewDropdown';
import { FilterToggle } from '@g17eco/molecules/filter-toggle';
import { SearchQuestions } from './partials/SearchQuestions';
import { SurveySettings } from '@g17eco/types/survey';
import { SurveyOverviewMode } from '@g17eco/types/surveyOverview';
import { FilterQuestions } from './partials/FilterQuestions';
import './styles.scss';
import { SurveyScope } from '@g17eco/types/surveyCommon';

type ToolbarComponent = (props: ToolbarComponentCommonProps) => React.JSX.Element | null;

export interface SurveyQuestionListToolbarProps<Settings extends Partial<SurveySettings> = Partial<SurveySettings>> {
  surveyId?: string;
  initiativeId: string;
  handleChangeSettings?: (key: keyof Settings, setting: string | string[] | boolean) => void;
  settings?: Settings;
  components?: {
    top?: ToolbarComponent[];
    mid?: ToolbarComponent[];
    bottom?: ToolbarComponent[];
  };
  toggleExpandAll: () => void;
  isExpanded: boolean;
  toggleSelectAll: () => void;
  isSelectAll: boolean;
  toggleShowingBookmarks?: (hasFilterByBookmarks: boolean) => void;
  isShowingBookmarks?: boolean;
  defaultOverviewMode?: SurveyOverviewMode;
  handleSelectOverviewMode?: (view: ScopeView) => void;
  surveyScope: SurveyScope | undefined;
}

export interface ToolbarComponentCommonProps<Settings extends Partial<SurveySettings> = Partial<SurveySettings>> extends SurveyQuestionListToolbarProps<Settings> {
  toggleFilters: () => void;
  showExtendedFilters?: boolean;
}

const defaultComponents: SurveyQuestionListToolbarProps['components'] = {
  top: [SearchQuestions, ScopeViewDropdown],
  bottom: [FilterToggle],
};

export const SurveyQuestionListToolbar = (props: SurveyQuestionListToolbarProps) => {
  const { settings, handleChangeSettings, components = defaultComponents, surveyScope, initiativeId } = props;

  // Only check for extended filters that are used in <FilterQuestions />, otherwise we expand and render nothing
  const [showExtendedFilters, setShowExtendedFilters] = useState(settings ? hasExtendedFilter(settings) : false);

  const toggleFilters = () => setShowExtendedFilters(!showExtendedFilters);

  const toolbarProps = {
    ...props,
    toggleFilters
  };

  const { top = [], mid = [], bottom = [] } = components;

  return (
    <div className='py-2'>
      <div className='surveyQuestionListToolbar pb-2 d-flex flex-wrap gap-2 gap-md-3 align-items-center justify-content-between'>
        <div className='flex-grow-1 d-flex flex-wrap gap-2 align-items-center justify-content-between'>
          {top.map((el, ix) =>
            el ? React.createElement(el, { ...toolbarProps, key: `cp-${ix}` }) : null
          )}
        </div>
        {mid.length ? (
          <div className='flex-grow-1 flex-md-grow-0 d-flex flex-wrap gap-3 align-items-center justify-content-between'>
            {mid.map((el, ix) =>
              el ? React.createElement(el, { ...toolbarProps, key: `cp-${ix}` }) : null
            )}
          </div>
        ) : null}
      </div>
      <div className={'d-flex flex-column flex-sm-row align-items-center justify-content-between flex-wrap'}>
        {bottom.map((el, ix) => (el ? React.createElement(el, { ...toolbarProps, showExtendedFilters, key: `cp-${ix}` }) : null))}
        {showExtendedFilters && settings && handleChangeSettings ? (
          <div className='w-100 px-1 py-2 order-2'>
            <FilterQuestions
              initiativeId={initiativeId}
              settings={settings}
              handleChangeSettings={handleChangeSettings}
              surveyScope={surveyScope}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
